import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import api from '../../utils/api';

const font = "'Outfit', sans-serif";
const BL = '#6366f1';
const C = { text: '#1e1b4b', sub: '#6b7280', border: '#e0e7ff', bg: '#eef2ff', white: '#fff' };

const ManageHalls = () => {
    const [halls, setHalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ hall_number: '', capacity: '', building: '' });

    const fetchHalls = async () => {
        try {
            const res = await api.get('/api/hall-manager/halls');
            setHalls(res.data.halls);
        } catch (e) {
            setError('Failed to load halls');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHalls();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        setError(''); setMsg('');
        try {
            if (editingId) {
                await api.put(`/api/hall-manager/halls/${editingId}`, form);
                setMsg('Hall updated successfully');
            } else {
                await api.post('/api/hall-manager/halls', form);
                setMsg('Hall added successfully');
            }
            setForm({ hall_number: '', capacity: '', building: '' });
            setEditingId(null);
            fetchHalls();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save hall');
        }
    };

    const handleEdit = (h) => {
        setEditingId(h.id);
        setForm({ hall_number: h.hall_number, capacity: h.capacity, building: h.building });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this hall?')) return;
        setError(''); setMsg('');
        try {
            await api.delete(`/api/hall-manager/halls/${id}`);
            fetchHalls();
            setMsg('Hall deleted successfully');
            if (editingId === id) {
                setEditingId(null);
                setForm({ hall_number: '', capacity: '', building: '' });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete hall');
        }
    };

    const handleCancel = () => {
        setEditingId(null);
        setForm({ hall_number: '', capacity: '', building: '' });
        setError(''); setMsg('');
    };

    return (
        <div style={{ fontFamily: font }}>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: C.text, margin: '0 0 8px', letterSpacing: -0.5 }}>Manage Examination Halls</h1>
            <p style={{ color: C.sub, fontSize: 14, margin: '0 0 30px' }}>Add physical rooms, specify their total capacity, and track building locations for student allocation.</p>

            {error && <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 12, marginBottom: 20, fontSize: 13, fontWeight: 600 }}>{error}</div>}
            {msg && <div style={{ padding: '12px 16px', background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#059669', borderRadius: 12, marginBottom: 20, fontSize: 13, fontWeight: 600 }}>{msg}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(350px, 2fr)', gap: 30, alignItems: 'start' }}>

                {/* Form Section */}
                <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 4px 12px rgba(99,102,241,0.05)' }}>
                    <div style={{ padding: '16px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, fontWeight: 800, fontSize: 14, color: C.text }}>
                        {editingId ? '✏️ Edit Hall Details' : '➕ Add New Hall'}
                    </div>
                    <form onSubmit={handleSave} style={{ padding: 24 }}>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: BL, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>Hall / Room Number *</label>
                            <input value={form.hall_number} onChange={e => setForm({ ...form, hall_number: e.target.value })} required placeholder="e.g. Room 402" style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: BL, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>Seating Capacity *</label>
                            <input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} required placeholder="e.g. 60" style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: BL, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>Building Name</label>
                            <input value={form.building} onChange={e => setForm({ ...form, building: e.target.value })} placeholder="e.g. Main Block" style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13, fontFamily: font, outline: 'none', boxSizing: 'border-box' }} />
                        </div>

                        <div style={{ display: 'flex', gap: 10 }}>
                            {editingId && (
                                <button type="button" onClick={handleCancel} style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.bg, color: C.text, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: font }}>
                                    Cancel
                                </button>
                            )}
                            <button type="submit" style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#818cf8,#6366f1)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                {editingId ? <><FiEdit2 size={14} /> Update Hall</> : <><FiPlus size={14} /> Add Hall</>}
                            </button>
                        </div>
                    </form>
                </div>

                {/* List Section */}
                <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 4px 12px rgba(99,102,241,0.05)' }}>
                    <div style={{ padding: '16px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, fontWeight: 800, fontSize: 14, color: C.text, display: 'flex', justifyContent: 'space-between' }}>
                        <span>🏢 Existing Halls</span>
                        <span style={{ color: BL, fontSize: 12 }}>{halls.length} Total</span>
                    </div>

                    {loading ? (
                        <div style={{ padding: 40, textAlign: 'center', color: C.sub, fontSize: 13 }}>Loading halls...</div>
                    ) : halls.length === 0 ? (
                        <div style={{ padding: 40, textAlign: 'center', color: C.sub, fontSize: 13 }}>No halls added yet. Add your first exam hall.</div>
                    ) : (
                        <div style={{ padding: '10px 0', maxHeight: 500, overflowY: 'auto' }}>
                            {halls.map(h => (
                                <div key={h.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 800, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {h.hall_number}
                                            <span style={{ fontSize: 11, fontWeight: 700, background: '#ecfdf5', color: '#059669', padding: '2px 8px', borderRadius: 20 }}>{h.capacity} Seats</span>
                                        </div>
                                        <div style={{ fontSize: 12, color: C.sub, marginTop: 4 }}>{h.building || 'No building specified'}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button onClick={() => handleEdit(h)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: C.bg, color: BL, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}>
                                            <FiEdit2 size={14} />
                                        </button>
                                        <button onClick={() => handleDelete(h.id)} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}>
                                            <FiTrash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>

            {/* Visual Hall View Section */}
            {halls.length > 0 && (
                <div style={{ marginTop: 40, background: C.white, borderRadius: 24, border: `1.5px solid ${C.border}`, padding: 30, boxShadow: '0 10px 30px rgba(0,0,0,0.02)' }}>
                    <h2 style={{ fontSize: 20, fontWeight: 900, color: C.text, margin: '0 0 8px' }}>Visual Seating Layout</h2>
                    <p style={{ color: C.sub, fontSize: 13, margin: '0 0 24px' }}>A representational view of the examination room seating structure for planning student allocations.</p>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
                        {halls.map(h => (
                            <div key={h.id} style={{ padding: 20, borderRadius: 16, border: `1px solid ${C.border}`, background: '#fcfdfe' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                    <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Hall {h.hall_number}</h3>
                                    <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', background: C.bg, color: BL, borderRadius: 8 }}>{h.capacity} Max Seats</span>
                                </div>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 4 }}>
                                    {Array.from({ length: Math.min(parseInt(h.capacity), 50) }).map((_, i) => (
                                        <div key={i} title={`Seat ${i+1}`} style={{ aspectRatio: '1/1', borderRadius: 4, background: '#cbd5e1', opacity: 0.5 }}></div>
                                    ))}
                                    {parseInt(h.capacity) > 50 && (
                                        <div style={{ gridColumn: 'span 10', textAlign: 'center', fontSize: 10, color: C.sub, marginTop: 4, fontWeight: 600 }}>+ {parseInt(h.capacity) - 50} more seats ...</div>
                                    )}
                                </div>
                                <div style={{ marginTop: 12, height: 6, background: BL, borderRadius: 3, width: '40%', marginInline: 'auto' }}></div>
                                <div style={{ textAlign: 'center', fontSize: 9, color: BL, fontWeight: 700, textTransform: 'uppercase', marginTop: 4 }}>Invigilator Desk / Board</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageHalls;
